import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { compareTextIn } from './format'
import {
  activeFilters,
  filterRows,
  nextSort,
  NO_VIEW,
  sortRows,
  type SortDirection,
  type TableColumn,
  type TableViewState,
} from './tableView'

/** A table's rows as shown, and what its headings and filter boxes need to draw themselves. */
export interface TableView<Row> {
  /** The rows the filters keep, in the order asked for. */
  rows: Row[]
  /** How many rows there are before any filter. */
  total: number
  /** How many columns narrow the rows right now. */
  active: number
  state: TableViewState
  directionOf: (column: string) => SortDirection | null
  toggleSort: (column: string) => void
  filterOf: (column: string) => string
  setFilter: (column: string, value: string) => void
  clearFilters: () => void
  /** Puts back a view kept earlier, or none. */
  replace: (state: TableViewState) => void
}

export interface TableViewOptions<Row> {
  /** The view to start from — one a page kept, in its address or for its tab. */
  initial?: TableViewState
  /** The group a row belongs to, when the table's rows fall in groups (see `sortRows`). */
  rank?: (row: Row) => number
  /** Told of every change the person makes — a page that keeps its view writes it down here. */
  onChange?: (state: TableViewState) => void
}

/**
 * Sorting and filtering a table by its columns (TRKC-110). The state starts from `initial`,
 * and every change the person makes is handed to `onChange` — a list keeps it in its address
 * (`useAddressTableView`), the DAT paste for its tab.
 *
 * <p>
 * Words sort the way the screen's language sorts them, so the order follows the language switch.
 * </p>
 */
export function useTableView<Row>(
  rows: readonly Row[],
  columns: readonly TableColumn<Row>[],
  { initial = NO_VIEW, rank, onChange }: TableViewOptions<Row> = {},
): TableView<Row> {
  const { i18n } = useTranslation()
  const [state, setState] = useState<TableViewState>(initial)
  const compareText = useMemo(() => compareTextIn(i18n.language), [i18n.language])

  const filtered = useMemo(() => filterRows(rows, columns, state.filters), [rows, columns, state.filters])
  const shown = useMemo(
    () => sortRows(filtered, columns, state.sort, compareText, rank),
    [filtered, columns, state.sort, compareText, rank],
  )

  // A person's change: kept here, and handed to whoever writes the view down.
  const change = (next: TableViewState) => {
    setState(next)
    onChange?.(next)
  }

  return {
    rows: shown,
    total: rows.length,
    active: activeFilters(columns, state.filters),
    state,
    directionOf: (column) => (state.sort?.column === column ? state.sort.direction : null),
    toggleSort: (column) => change({ ...state, sort: nextSort(state.sort, column) }),
    filterOf: (column) => state.filters[column] ?? '',
    setFilter: (column, value) => change({ ...state, filters: { ...state.filters, [column]: value } }),
    clearFilters: () => change({ ...state, filters: {} }),
    replace: setState,
  }
}
